import { Component, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import Lenis from 'lenis';
import { LanguageRoot, VersionScope } from '@/i18n/LanguageContext';
import { LANG_CODES, findRouteKey } from '@/i18n/routes';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import CookieBanner from '@/components/CookieBanner';
import LangGate from '@/pages/LangGate';
import Home from '@/pages/Home';
import Solutions from '@/pages/Solutions';
import Products from '@/pages/Products';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import Passengers from '@/pages/Passengers';
import Faq from '@/pages/Faq';
import Legal from '@/pages/Legal';
import Admin from '@/pages/Admin';
import NotFound from '@/pages/NotFound';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen items-center justify-center p-8 text-center">
          <div>
            <p className="font-display text-xl font-bold text-brand-navy">Algo deu errado.</p>
            <button data-testid="error-reload-btn" className="mt-4 rounded-full bg-brand-blue px-6 py-2.5 text-white" onClick={() => window.location.reload()}>Recarregar</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function VersionRouter() {
  const { lang } = useParams();
  const location = useLocation();
  const sub = location.pathname.replace(`/${lang}`, '').replace(/^\/+/, '');
  const key = findRouteKey(lang, sub);
  if (key === null) return <NotFound />;
  switch (key) {
    case 'home': return <Home />;
    case 'solutions': return <Solutions />;
    case 'products': return <Products />;
    case 'about': return <About />;
    case 'contact': return <Contact />;
    case 'passengers': return <Passengers />;
    case 'faq': return <Faq />;
    case 'privacy': return <Legal kind="privacy" />;
    case 'terms': return <Legal kind="terms" />;
    default: return <NotFound />;
  }
}

function VersionLayout() {
  const { lang } = useParams();
  if (!LANG_CODES.includes(lang)) return <NotFound />;
  return (
    <VersionScope lang={lang}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1">
          <VersionRouter />
        </main>
        <Footer />
        <WhatsAppFloat />
        <CookieBanner />
      </div>
    </VersionScope>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => { lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <LanguageRoot>
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LangGate />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/:lang/*" element={<VersionLayout />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </LanguageRoot>
  );
}

export default App;